import React, { useState, useEffect, useMemo } from 'react';
import CreateMeetingModal from '../components/CreateMeetingModal';
import MeetingDetailsModal from '../components/MeetingDetailsModal';
import ExecutiveMeetingTable from '../components/ExecutiveMeetingTable'; // Added ExecutiveMeetingTable
import dataService from '../services/dataService';
import { useAuth } from '../context/AuthContext';
import { PlusIcon, CalendarIcon, ClockIcon } from '@heroicons/react/24/outline'; // Modified icon imports
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

const Meetings = () => {
    const { user } = useAuth();
    const meetingLabel = user?.namingConvention?.meetingLabel || 'Meeting';
    const activeRole = sessionStorage.getItem('activeRole') || user?.role;
    const isMember = activeRole === 'member';
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState(null);
    const [selectedMeetingForEdit, setSelectedMeetingForEdit] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    // Sub-Tabs for Upcoming/Previous
    const [timeTab, setTimeTab] = useState('upcoming'); // 'upcoming' or 'previous'

    useEffect(() => {
        fetchMeetings();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [timeTab]);

    const fetchMeetings = async () => {
        try {
            const response = await dataService.getMeetings();
            setMeetings(response.data.data);
        } catch (error) {
            console.error(error);
            // toast.error('Failed to fetch meetings'); 
        } finally {
            setLoading(false);
        }
    };

    const handleRSVP = async (meetingId, status) => {
        try {
            await dataService.rsvpMeeting(meetingId, status);
            toast.success(`RSVP updated to ${status} `);
            fetchMeetings();
        } catch (error) {
            console.error(error);
            toast.error('Failed to update RSVP');
        }
    };

    const handleDelete = async (meetingId) => {
        if (!window.confirm('Are you sure you want to delete this meeting?')) return;
        try {
            await dataService.deleteMeeting(meetingId);
            toast.success('Meeting deleted successfully');
            fetchMeetings();
        } catch (error) {
            console.error(error);
            toast.error('Failed to delete meeting');
        }
    };

    const handleSuccess = (data, isEdit) => {
        setIsCreateModalOpen(false);
        setSelectedMeetingForEdit(null);
        fetchMeetings();
        if (isEdit) {
            toast.success('Meeting updated successfully');
        } else {
            toast.success('Meeting scheduled successfully');
        }
    };

    const handleEdit = (meeting) => {
        setSelectedMeetingForEdit(meeting);
        setIsCreateModalOpen(true);
    };

    const handleCreateModalClose = () => {
        setIsCreateModalOpen(false);
        setSelectedMeetingForEdit(null);
    };

    const handleViewDetails = (meeting) => {
        setSelectedMeeting(meeting);
        setIsDetailsModalOpen(true);
    };

    const filteredMeetings = useMemo(() => {
        const now = new Date();
        let filtered = [...meetings];

        // Filter by Time Tab
        if (timeTab === 'upcoming') {
            filtered = filtered.filter(m => new Date(m.meeting_date) >= now);
            filtered.sort((a, b) => new Date(a.meeting_date) - new Date(b.meeting_date));
        } else {
            filtered = filtered.filter(m => new Date(m.meeting_date) < now);
            filtered.sort((a, b) => new Date(b.meeting_date) - new Date(a.meeting_date));
        }

        return filtered;
    }, [meetings, timeTab]);

    const itemsPerPage = 12;
    const totalPages = Math.ceil(filteredMeetings.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedMeetings = filteredMeetings.slice(startIndex, endIndex);

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">
                        Member {meetingLabel}s
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                        <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <p className="text-[12px] font-bold text-slate-400 uppercase tracking-widest">
                            1-to-1 Interactions
                        </p>
                    </div>
                </div>
                {isMember && (
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-2xl text-[13px] font-bold uppercase tracking-widest shadow-xl shadow-primary-200 hover:bg-primary-700 transition-all active:scale-95"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Schedule 1-to-1
                    </button>
                )}
            </div>

            {/* Sub Tabs (Upcoming/Previous) */}
            <div className="flex border-b border-slate-100 mb-2">
                {[
                    { id: 'upcoming', label: 'Upcoming', icon: CalendarIcon },
                    { id: 'previous', label: 'History', icon: ClockIcon }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setTimeTab(tab.id)}
                        className={twMerge(
                            "px-6 py-4 text-[13px] font-bold tracking-tight transition-all relative flex items-center gap-2",
                            timeTab === tab.id
                                ? "text-primary-600"
                                : "text-slate-400 hover:text-slate-600"
                        )}
                    >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                        {timeTab === tab.id && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 rounded-full" />
                        )}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-sm border border-slate-100">
                    <div className="w-10 h-10 rounded-full border-4 border-slate-100 border-t-primary-600 animate-spin mb-4" />
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Loading Meetings...</p>
                </div>
            ) : filteredMeetings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
                    <div className="p-5 bg-slate-50 rounded-full mb-4">
                        <CalendarIcon className="w-10 h-10 text-slate-300" />
                    </div>
                    <p className="text-slate-900 font-black tracking-tight text-lg">No meetings details</p>
                    <p className="text-slate-400 font-bold text-sm">No {timeTab} 1-to-1 meetings found.</p>
                </div>
            ) : (
                <>
                    {/* Card Grid View replaced by Executive Table */}
                    <ExecutiveMeetingTable
                        meetings={paginatedMeetings}
                        onView={handleViewDetails}
                        onRSVP={handleRSVP}
                        onDelete={handleDelete}
                        onEdit={handleEdit}
                        context="member"
                    />

                    {/* Pagination Controls */}
                    <div className="flex flex-col md:flex-row items-center justify-between mt-6 pt-6 border-t border-slate-100 gap-4">
                        <div className="text-sm font-semibold text-slate-600">
                            Showing <span className="font-black text-slate-900">{Math.min(endIndex, filteredMeetings.length)}</span> of{' '}
                            <span className="font-black text-slate-900">{filteredMeetings.length}</span> {filteredMeetings.length === 1 ? 'meeting' : 'meetings'}
                        </div>

                        {filteredMeetings.length > itemsPerPage && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === 1
                                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                        : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                        }`}
                                >
                                    Previous
                                </button>

                                <div className="flex items-center gap-1">
                                    {[...Array(totalPages)].map((_, index) => {
                                        const pageNum = index + 1;
                                        if (
                                            pageNum === 1 ||
                                            pageNum === totalPages ||
                                            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`w-10 h-10 rounded-xl font-black text-sm transition-all ${currentPage === pageNum
                                                        ? 'bg-[#4eb7f5] text-white shadow-lg shadow-blue-100'
                                                        : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                            return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                                        }
                                        return null;
                                    })}
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === totalPages
                                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                        : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Modals */}
            <MeetingDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                meeting={selectedMeeting}
            />

            <CreateMeetingModal
                isOpen={isCreateModalOpen}
                onClose={handleCreateModalClose}
                onSuccess={handleSuccess}
                meetingToEdit={selectedMeetingForEdit}
            />
        </div>
    );
};

export default Meetings;
